import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';

import { EmptySelect } from './emptySelect';

import type { AzureDiskConfigState } from '../restore/restoreAzureVMWizard';

const title = 'Virtual Machine Size';

/**
 * Azure Disk Storage Account Types that require PremiumIO capability on the VM.
 * These values correspond to Azure SDK's DiskStorageAccountTypes enum.
 * See: shared/azure/vm/azure_vm.go for backend usage
 */
enum AzurePremiumStorageType {
  PremiumLRS = 'Premium_LRS',
  PremiumZRS = 'Premium_ZRS',
  PremiumV2LRS = 'PremiumV2_LRS',
  UltraSSDLRS = 'UltraSSD_LRS',
}

const PREMIUM_STORAGE_TYPES = Object.values(AzurePremiumStorageType);
export const VirtualMachineSizeSelect = ({
  accountId,
  location,
  vmSize,
  onChange,
  disksState,
  originalVMSize,
}: {
  accountId?: string;
  location?: string;
  vmSize?: string;
  disksState: AzureDiskConfigState[] | undefined;
  onChange: (vmSize: string) => void;
  originalVMSize: string | undefined;
}) => {
  if (!accountId || !location) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerVirtualMachineSizeSelect
      accountId={accountId}
      location={location}
      vmSize={vmSize}
      onChange={onChange}
      disksState={disksState}
      originalVMSize={originalVMSize}
    />
  );
};

const InnerVirtualMachineSizeSelect = ({
  accountId,
  location,
  vmSize,
  onChange,
  disksState,
  originalVMSize,
}: {
  accountId: string;
  location: string;
  vmSize?: string;
  disksState: AzureDiskConfigState[] | undefined;
  onChange: (vmSize: string) => void;
  originalVMSize: string | undefined;
}) => {
  const dal = useDAL();
  const { isDemo, isDev } = useEnvironment();
  const { data: body } = dal.restore.azureRestoreConfig.useListSKUs(
    accountId,
    location,
    ResourceType.AzureVirtualMachine
  );

  const SKUChoices = useMemo(() => {
    const dataDisksLength = (disksState?.length ?? 1) - 1;
    const originalVMSKU = body?.skus?.find((x) => x.name === originalVMSize);
    const diskHyperVGenerations = Array.from(
      new Set(disksState?.map((d) => d.diskSettings.settings.hyperVGeneration))
    ).filter(Boolean);

    // Check if any disk uses premium storage (type contains "Premium" or is UltraSSD)
    // The restore flow defaults to Premium_LRS, so if no type is set, assume premium
    const requiresPremiumStorage =
      !disksState ||
      disksState.length === 0 ||
      disksState.some((d) => {
        const diskType = d.diskSettings.settings.type;
        // If disk type is not set, the backend defaults to Premium_LRS
        if (!diskType) return true;
        return PREMIUM_STORAGE_TYPES.some((premiumType) =>
          diskType.includes(premiumType)
        );
      });

    return (body?.skus ?? [])
      .filter((sku) => {
        if (isDemo || isDev) {
          return true;
        }
        const skuGenerations = sku.capabilities?.HyperVGenerations?.split(',');

        // Filter out VM sizes that don't support Premium storage when required
        // Note: PremiumIO is returned as string 'True'/'False' from Azure API, not boolean
        const supportsPremiumStorage =
          sku.capabilities?.PremiumIO?.toLowerCase() === 'true';
        if (requiresPremiumStorage && !supportsPremiumStorage) {
          return false;
        }

        return (
          (sku.capabilities?.CpuArchitectureType ?? '') ===
            (originalVMSKU?.capabilities?.CpuArchitectureType ?? '') &&
          Number(sku.capabilities?.MaxDataDiskCount ?? '') >= dataDisksLength &&
          diskHyperVGenerations.every(
            (g) =>
              typeof g === 'string' &&
              Array.isArray(skuGenerations) &&
              skuGenerations.includes(g)
          )
        );
      })
      .map((sku) => sku.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disksState, body?.skus, originalVMSize]);

  if (!body?.skus?.length) {
    return <EmptySelect title={title} />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        clearOnBlur
        size='small'
        value={vmSize || null}
        onChange={(event, newValue) => onChange(newValue || '')}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              sx={{
                '& .MuiInputBase-input': {
                  height: '40px',
                },
              }}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={SKUChoices}
      />
    </FormControl>
  );
};
